import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, X, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { API, productImage, buildOrderWhatsAppLink } from "../../lib/site";

export const Store = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const add = (p) => {
    setCart((c) => {
      const found = c.find((i) => i.id === p.id);
      if (found) return c.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
    toast.success(`${p.name} added to cart`);
  };
  const dec = (id) => setCart((c) => c.flatMap((i) => (i.id === id ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i])));
  const inc = (id) => setCart((c) => c.map((i) => (i.id === id ? { ...i, qty: i.qty + 1 } : i)));
  const remove = (id) => setCart((c) => c.filter((i) => i.id !== id));

  const count = cart.reduce((s, i) => s + i.qty, 0);
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const checkout = () => {
    if (!cart.length) return;
    window.open(buildOrderWhatsAppLink(cart), "_blank");
  };

  if (!products.length) return null;

  return (
    <section id="shop" className="relative py-24 md:py-36 max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A24B] mb-4">The Shop</p>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight text-[#FAFAFA]">
            Grooming <span className="italic gold-gradient-text">accessories</span>
          </h2>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="open-cart-btn"
          className="relative inline-flex items-center gap-3 px-6 py-3.5 border border-[#C9A24B]/40 text-[#FAFAFA] text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-[#C9A24B] hover:text-black transition-colors duration-300"
        >
          <ShoppingBag size={16} /> Cart
          {count > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#C9A24B] text-black text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.06 }}
            className="group bg-[#141414] border border-white/10 rounded-sm overflow-hidden flex flex-col"
            data-testid={`product-card-${p.id}`}
          >
            <div className="relative aspect-square overflow-hidden bg-[#0A0A0A]">
              <img src={productImage(p)} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-[1.06]" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <h3 className="font-serif-display text-2xl text-[#FAFAFA] mb-1">{p.name}</h3>
              <p className="text-xs text-[#8A8A8A] font-light flex-1 leading-relaxed">{p.description}</p>
              <div className="flex items-center justify-between mt-5">
                <span className="text-[#C9A24B] text-lg">${p.price.toFixed(2)}</span>
                <button
                  onClick={() => add(p)}
                  data-testid={`add-to-cart-${p.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#C9A24B] text-black text-[10px] uppercase tracking-[0.2em] font-semibold rounded-sm hover:bg-[#d9b463] transition-colors duration-300"
                >
                  <Plus size={13} /> Add
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="bg-[#141414] border-l border-[#C9A24B]/30 text-[#FAFAFA] w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
          <SheetHeader>
            <SheetTitle className="font-serif-display text-3xl font-light text-[#FAFAFA] text-left">Your cart</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto custom-scrollbar mt-6 space-y-4">
            {cart.length === 0 && <p className="text-[#8A8A8A] text-sm">Your cart is empty.</p>}
            <AnimatePresence>
              {cart.map((i) => (
                <motion.div
                  key={i.id}
                  layout
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                  className="flex items-center justify-between gap-4 border-b border-white/10 pb-4"
                  data-testid={`cart-item-${i.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-[#FAFAFA] truncate">{i.name}</p>
                    <p className="text-xs text-[#C9A24B]">${(i.price * i.qty).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => dec(i.id)} className="w-7 h-7 border border-white/15 rounded-sm flex items-center justify-center text-[#8A8A8A] hover:text-[#FAFAFA]"><Minus size={13} /></button>
                    <span className="w-6 text-center text-sm">{i.qty}</span>
                    <button onClick={() => inc(i.id)} className="w-7 h-7 border border-white/15 rounded-sm flex items-center justify-center text-[#8A8A8A] hover:text-[#FAFAFA]"><Plus size={13} /></button>
                    <button onClick={() => remove(i.id)} className="ml-1 text-[#8A8A8A] hover:text-red-400"><X size={15} /></button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="border-t border-white/10 pt-5 mt-4">
            <div className="flex items-center justify-between mb-5">
              <span className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">Total</span>
              <span className="font-serif-display text-2xl text-[#C9A24B]" data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <button
              onClick={checkout}
              disabled={!cart.length}
              data-testid="checkout-btn"
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A24B] text-black text-xs uppercase tracking-[0.2em] font-semibold rounded-sm hover:bg-[#d9b463] transition-colors duration-300 disabled:opacity-50"
            >
              <MessageCircle size={16} /> Order via WhatsApp
            </button>
            <p className="text-[11px] text-center text-[#8A8A8A] mt-3">We'll confirm price & pickup on WhatsApp.</p>
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
};
